import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { useCategoriesQuery } from '../products/api';

import type { CategoryFormValues } from './schemas';
import type { Category } from './types';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/**
 * `GET /merchant/categories` already exists (`features/products/api.ts`,
 * built for `ProductForm`'s category picker) — re-exported here rather
 * than duplicated, so this screen and the product form share one cache
 * entry (`['categories', activeShopId]`) and one network call.
 */
export { useCategoriesQuery };

function categoryToBody(values: CategoryFormValues): Record<string, unknown> {
  return {
    name: values.name,
    slug: values.slug,
    description: values.description ?? null,
    imageUrl: values.imageUrl ?? null,
    parentCategoryId: values.parentCategoryId ?? null,
    isActive: values.isActive,
  };
}

export function useCreateCategoryMutation(): UseMutationResult<
  Category,
  ApiError,
  CategoryFormValues
> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: CategoryFormValues) =>
      apiRequest<Category>('/merchant/categories', {
        method: 'POST',
        body: categoryToBody(values),
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['categories', activeShopId] });
    },
  });
}

/**
 * `PUT` is a full-state replace (the same posture `ProductForm`'s own
 * `useUpdateProductMutation` already takes) — the id is part of the
 * mutate-time argument, not a hook parameter, since `CategoryListPage` is
 * a single list+modal screen where the edited category varies per click
 * (mirrors `useUpdateCouponMutation`'s own shape).
 */
export function useUpdateCategoryMutation(): UseMutationResult<
  Category,
  ApiError,
  { categoryId: string; values: CategoryFormValues }
> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ categoryId, values }) =>
      apiRequest<Category>(`/merchant/categories/${categoryId}`, {
        method: 'PUT',
        body: categoryToBody(values),
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['categories', activeShopId] });
    },
  });
}

/**
 * Soft-delete (`DELETE /merchant/categories/{id}`, `category-repository.ts`'s
 * own `softDelete`) — refused by the API with a real 409 when the category
 * still has live child categories (`CategoryHasChildrenError`). Nothing
 * here re-implements that check; the caller relays the API's own message.
 */
export function useDeleteCategoryMutation(): UseMutationResult<null, ApiError, string> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (categoryId: string) =>
      apiRequest<null>(`/merchant/categories/${categoryId}`, {
        method: 'DELETE',
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['categories', activeShopId] });
    },
  });
}
